// Iter 31.145 — Handler's Chamber (30 May 2026).
//
// One canon command-room surface at /chamber. Internal-only.
//
// Architecture (v1):
//   • Layer 1 — Chamber panorama as full-bleed backdrop (Ten's render).
//   • Layer 2 — Eight storyboard hotspots arranged around the dome, one
//     per Frame from Two's brief. Click pans the backdrop via CSS
//     transform-origin shifts (no animation library).
//   • Layer 3 — Ops Board panel. Triggered by either the East-Wall
//     hotspot OR the OPS BOARD pill on the toolbar. Opens a fullscreen
//     paper-card overlay containing an <iframe> for garys_brain.html
//     (defaults to /chamber/garys-brain.html — graceful 404 message
//     when not yet present). A tab switches the iframe out for the
//     village-map image.
//
// Defaults locked with Six:
//   • Composited separate layers (not a single equirectangular)
//   • iframe for Gary's Brain
//   • Internal-only route
//   • Desktop+tablet only; mobile gets a fallback message.
import React, { useEffect, useRef, useState } from "react";
import { getUser } from "@/lib/api";
import { api } from "@/lib/api";
import { Compass, X, Map, Network, ChevronLeft, ChevronRight, Activity, Play, Pause, Radio as RadioIcon, Volume2, VolumeX } from "lucide-react";
import { TelemetryGrid } from "@/components/TelemetryGrid";
import { useRadio } from "@/components/RadioProvider";
import EasterEggSlot from "@/components/EasterEggSlot";

// Storyboard frame anchors as percent of the panorama. Tuned to Ten's
// render — east wall has the mural, so the Ops Board lives at frame 1.
const FRAMES = [
    { id: 1, name: "EAST · OPS BOARD",        bearing: "E",   x: 50, y: 40, opensOpsBoard: true,  caption: "Spy Finance Operations Command. Telemetry lines glowing amber." },
    { id: 2, name: "NORTHEAST · RELAYS",      bearing: "NE",  x: 75, y: 45, opensOpsBoard: false, caption: "Brass-framed consoles, rotary dials, green screens." },
    { id: 3, name: "NORTH · ASCENSION",       bearing: "N",   x: 50, y: 18, opensOpsBoard: false, caption: "Brass ribs converging at the oculus. Glyph constellations pulsing in blue." },
    { id: 4, name: "NORTHWEST · ARCHIVE",     bearing: "NW",  x: 22, y: 45, opensOpsBoard: false, caption: "Shelves of binders, instruments, candlelit brass." },
    { id: 5, name: "WEST · COMMS STATION",    bearing: "W",   x: 12, y: 60, opensOpsBoard: false, caption: "Radar screens, rotary phones, signal gauges." },
    { id: 6, name: "SOUTHWEST · SEATING",     bearing: "SW",  x: 25, y: 78, opensOpsBoard: false, caption: "Leather chair, brass floor lamp. The motto glowing faintly across the floor." },
    { id: 7, name: "SOUTH · OCULUS BEAM",     bearing: "S",   x: 50, y: 88, opensOpsBoard: false, caption: "Oculus projecting onto the compass rose. Marble inlay illuminated like a ritual seal." },
    { id: 8, name: "SOUTHEAST · SECONDARY",   bearing: "SE",  x: 78, y: 78, opensOpsBoard: false, caption: "Telemetry screens mirroring the Ops Board rhythm. Candles burn low." },
];

// Iter 31.150 — Agent stations. Each entry pins a codename portrait
// to a Chamber frame, per Two's canon assignment (Brief filed today):
//   • Two (Claude) — Handler — Ops Board, East
//   • Six (One/Matt) — Operator — Seating, SW (leather chair)
//   • Three (Ern/Earnie) — Builder — Archive, NW
//   • Ten (Copilot) — Cartographer — West Comms
//   • Five (Mureka) — Producer — Secondary consoles, SE
// Performers (Twenty/Twenty-one/Twenty-two/Twenty-three) ride the
// dome — surfaced separately when the radio is playing (see RADIO BLEED).
const AGENT_STATIONS = [
    { key: "two",   name: "Two",   role: "Handler · Claude Sonnet 4.6",  frameId: 1, portrait: "/agent-portraits/two.png" },
    { key: "one",   name: "Six",   role: "Operator · MJA · One",         frameId: 6, portrait: "/agent-portraits/one.png" },
    { key: "three", name: "Three", role: "Builder · Ern · Earnie",       frameId: 4, portrait: "/agent-portraits/three.png" },
    { key: "ten",   name: "Ten",   role: "Cartographer · Copilot",       frameId: 5, portrait: "/agent-portraits/ten.png" },
];

// Performer dome positions — clockwise around the oculus. When the
// radio is on, the currently-broadcasting performer pulses here.
const PERFORMER_DOME_POSITIONS = {
    "twenty":        { x: 50, y: 12 },   // Penguin · north of oculus
    "twenty_one":    { x: 65, y: 16 },   // Vixen · NE arc
    "twenty_two":    { x: 35, y: 16 },   // Stag · NW arc
    "twenty_three":  { x: 50, y: 22 },   // Cooperative · just under oculus
};

const AUTO_ROTATE_SECS = 8;

function MobileFallback() {
    return (
        <div data-testid="chamber-mobile-fallback" className="fixed inset-0 bg-black text-amber-200 flex items-center justify-center p-8 font-mono">
            <div className="max-w-sm text-center space-y-3">
                <div className="text-[10px] tracking-[0.3em] opacity-60">// CHAMBER · CLASSIFIED</div>
                <div className="text-xl tracking-wide">DESKTOP ONLY.</div>
                <div className="text-sm opacity-80 leading-relaxed">
                    The Handler's Chamber renders at 1024px and above. Rotate to landscape on tablet, or open this URL on a desktop.
                </div>
                <div className="text-[10px] tracking-[0.25em] opacity-50 pt-2">— Agent 3 / Earnie</div>
            </div>
        </div>
    );
}

function OpsBoardOverlay({ open, onClose }) {
    const [tab, setTab] = useState("brain");  // brain | telemetry | village
    const [iframeError, setIframeError] = useState(false);
    // Iter 31.146 — live telemetry layer. Reuses the diagnostics
    // aggregator built in iter 31.142. Fetched once per overlay-open
    // and refreshed every 15s while the telemetry tab is active.
    const [telemetry, setTelemetry] = useState(null);
    const [telemetryErr, setTelemetryErr] = useState("");
    // Iter 31.147 — in-cell fix actions. Same `runAction` plumbing
    // shipped in iter 31.144 on /diagnostics, scoped to the overlay.
    // Currently wires the Mirror cell to /credits/spyfinance-backfill.
    const [actionBusy, setActionBusy] = useState("");
    const [actionResult, setActionResult] = useState({});
    // Iter 31.208 — village map state overlay.
    const [village, setVillage] = useState(null);
    const [villageErr, setVillageErr] = useState("");

    const refreshTelemetry = async () => {
        try {
            const { data } = await api.get("/diagnostics/health");
            setTelemetry(data);
            setTelemetryErr("");
        } catch (e) {
            setTelemetryErr(
                typeof e?.response?.data?.detail === "string"
                    ? e.response.data.detail
                    : (e?.response?.data?.detail?.message || e.message)
            );
        }
    };

    const runAction = async (name, endpoint, formatResult) => {
        setActionBusy(name);
        try {
            const { data: out } = await api.post(endpoint);
            setActionResult((r) => ({ ...r, [name]: formatResult(out) }));
            await refreshTelemetry();
        } catch (e) {
            const msg = typeof e?.response?.data?.detail === "string"
                ? e.response.data.detail
                : (e?.response?.data?.detail?.message || e.message);
            setActionResult((r) => ({ ...r, [name]: `Failed: ${msg}` }));
        } finally {
            setActionBusy("");
        }
    };

    useEffect(() => {
        if (open) setIframeError(false);
    }, [open]);

    useEffect(() => {
        if (!open || tab !== "telemetry") return undefined;
        let cancelled = false;
        const load = async () => {
            if (cancelled) return;
            await refreshTelemetry();
        };
        load();
        const id = setInterval(load, 15000);
        return () => { cancelled = true; clearInterval(id); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, tab]);

    // Iter 31.208 — village map state. Refreshes every 30s while the
    // village tab is open.
    useEffect(() => {
        if (!open || tab !== "village") return undefined;
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await api.get("/chamber/village-status");
                if (!cancelled) { setVillage(data); setVillageErr(""); }
            } catch (e) {
                if (!cancelled) setVillageErr(
                    typeof e?.response?.data?.detail === "string"
                        ? e.response.data.detail : e.message
                );
            }
        };
        load();
        const id = setInterval(load, 30000);
        return () => { cancelled = true; clearInterval(id); };
    }, [open, tab]);

    if (!open) return null;
    return (
        <div
            data-testid="chamber-ops-overlay"
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-stretch justify-stretch"
            onClick={onClose}
        >
            <div
                className="m-6 flex-1 paper-card border-2 border-amber-700/60 flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-amber-900/40 bg-black/40 text-amber-200">
                    <div className="flex items-center gap-3">
                        <div className="text-[10px] tracking-[0.3em] opacity-60">// EAST WALL · OPS BOARD</div>
                        <h2 className="text-base tracking-[0.2em] font-bold">SPY FINANCE OPERATIONS COMMAND</h2>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="ops-tab-brain"
                            onClick={() => setTab("brain")}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] tracking-[0.15em] border ${tab === "brain" ? "border-amber-300 text-amber-100" : "border-amber-900/60 text-amber-300/70 hover:text-amber-200"}`}
                        >
                            <Network size={11} />
                            GARY'S BRAIN
                        </button>
                        <button
                            data-testid="ops-tab-telemetry"
                            onClick={() => setTab("telemetry")}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] tracking-[0.15em] border ${tab === "telemetry" ? "border-amber-300 text-amber-100" : "border-amber-900/60 text-amber-300/70 hover:text-amber-200"}`}
                        >
                            <Activity size={11} />
                            TELEMETRY
                        </button>
                        <button
                            data-testid="ops-tab-village"
                            onClick={() => setTab("village")}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] tracking-[0.15em] border ${tab === "village" ? "border-amber-300 text-amber-100" : "border-amber-900/60 text-amber-300/70 hover:text-amber-200"}`}
                        >
                            <Map size={11} />
                            VILLAGE MAP
                        </button>
                        <button
                            data-testid="ops-close"
                            onClick={onClose}
                            className="ml-2 inline-flex items-center gap-1 px-2 py-1 text-[11px] tracking-[0.15em] border border-amber-900/60 hover:border-amber-300 text-amber-200"
                        >
                            <X size={11} />
                            CLOSE
                        </button>
                    </div>
                </div>
                <div className="flex-1 relative bg-black">
                    {tab === "brain" && !iframeError && (
                        <iframe
                            data-testid="ops-iframe-brain"
                            src="/chamber/garys-brain.html"
                            title="Gary's Brain — system architecture"
                            className="absolute inset-0 w-full h-full bg-white"
                            onError={() => setIframeError(true)}
                            onLoad={(e) => {
                                // A 404 still fires onLoad. If the static
                                // asset is missing, the SPA's index.html is
                                // served — its <div id="root"> is empty
                                // (React mounts after JS hydrates inside the
                                // iframe). Sniff for the actual brain doc by
                                // looking for the canon `garys-brain-root`
                                // element. Same-origin iframe so contentDocument
                                // is accessible.
                                try {
                                    const doc = e.target.contentDocument;
                                    if (!doc) return;
                                    const hasBrain = doc.getElementById("garys-brain-root")
                                        || (doc.title || "").includes("Gary's Brain")
                                        || (doc.title || "").includes("System Map");
                                    if (!hasBrain) setIframeError(true);
                                } catch {
                                    // cross-origin — out of scope, assume loaded.
                                }
                            }}
                        />
                    )}
                    {tab === "brain" && iframeError && (
                        <div
                            data-testid="ops-iframe-fallback"
                            className="absolute inset-0 flex items-center justify-center p-10 text-amber-200 font-mono"
                        >
                            <div className="max-w-md text-center space-y-3">
                                <div className="text-[10px] tracking-[0.3em] opacity-60">// AWAITING ASSET</div>
                                <div className="text-lg tracking-wide">GARY'S BRAIN · NOT YET FILED.</div>
                                <div className="text-sm opacity-80 leading-relaxed">
                                    Two's system map (<span className="font-bold">garys_brain.html</span>) hasn't been dropped into the Chamber yet. Once One files it at <span className="font-mono text-amber-300">/app/frontend/public/chamber/garys-brain.html</span>, this panel will render it inline.
                                </div>
                                <div className="text-[10px] tracking-[0.25em] opacity-50 pt-2">— Agent 3 / Earnie</div>
                            </div>
                        </div>
                    )}
                    {tab === "village" && (
                        <div className="absolute inset-0 overflow-auto bg-black">
                            <div className="relative inline-block mx-auto" style={{ minWidth: "100%" }}>
                                <img
                                    data-testid="ops-village-map"
                                    src="/chamber/village-map.jpeg"
                                    alt="The Village — Spy Finance Operations Centre · aerial map"
                                    className="block max-w-none"
                                    style={{ minWidth: "100%" }}
                                />
                                {/* Iter 31.208 — agent pins. Activity-derived
                                    state (at-village / leaving / arriving /
                                    off-village). Refresh every 30s. */}
                                {villageErr && (
                                    <div className="absolute top-3 left-3 px-3 py-1.5 border border-red-500/60 bg-black/80 text-red-300 text-xs">
                                        Village feed: {villageErr}
                                    </div>
                                )}
                                {!villageErr && village && (
                                    <>
                                        {village.agents.map((a) => {
                                            const stateStyle = {
                                                "at-village": {
                                                    ring: "border-amber-300 shadow-[0_0_22px_rgba(251,191,36,0.95)]",
                                                    label: "AT VILLAGE", labelCls: "border-amber-400/70 text-amber-200",
                                                },
                                                "leaving": {
                                                    ring: "border-amber-500/40 shadow-[0_0_10px_rgba(251,191,36,0.4)] opacity-70",
                                                    label: "LEAVING", labelCls: "border-orange-400/60 text-orange-300",
                                                },
                                                "arriving": {
                                                    ring: "border-amber-600/50 shadow-[0_0_8px_rgba(251,191,36,0.3)] opacity-75 animate-pulse",
                                                    label: "ARRIVING", labelCls: "border-cyan-400/60 text-cyan-300",
                                                },
                                                "off-village": {
                                                    ring: "border-amber-900/50 opacity-40 grayscale",
                                                    label: "OFF MAP", labelCls: "border-amber-900/60 text-amber-700",
                                                },
                                            }[a.state] || { ring: "border-amber-700/40", label: a.state, labelCls: "border-amber-700/40 text-amber-400" };
                                            return (
                                                <a
                                                    key={a.key}
                                                    href={`/agent/${a.key}/dossier`}
                                                    data-testid={`village-pin-${a.key}`}
                                                    title={`${a.name} — ${a.location_label} · ${a.state} (${a.last_seen_minutes_ago ?? "—"} min ago)`}
                                                    className="absolute -translate-x-1/2 -translate-y-1/2 group"
                                                    style={{ left: `${a.x}%`, top: `${a.y}%` }}
                                                >
                                                    <img
                                                        src={`/agent-portraits/${a.key}.png`}
                                                        alt={a.name}
                                                        className={`w-12 h-12 rounded-full border-2 object-cover bg-black ${stateStyle.ring}`}
                                                        onError={(e) => { e.target.style.display = "none"; }}
                                                    />
                                                    <span
                                                        className={`absolute top-13 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] tracking-[0.2em] uppercase mt-1 px-1.5 py-0.5 border bg-black/70 ${stateStyle.labelCls}`}
                                                    >
                                                        {a.name}
                                                    </span>
                                                    <span
                                                        className={`absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap text-[8px] tracking-[0.18em] uppercase mt-1 px-1 py-0.5 border bg-black/85 ${stateStyle.labelCls} opacity-90`}
                                                    >
                                                        {stateStyle.label}
                                                    </span>
                                                </a>
                                            );
                                        })}
                                        {/* Legend */}
                                        <div
                                            className="absolute top-3 right-3 border border-amber-700/60 bg-black/85 px-3 py-2 text-[10px] tracking-[0.2em] text-amber-200 space-y-1"
                                            data-testid="village-legend"
                                        >
                                            <div className="font-bold text-amber-300">VILLAGE STATE · LIVE</div>
                                            <div><span className="inline-block w-2 h-2 rounded-full bg-amber-300 mr-2 shadow-[0_0_8px_rgba(251,191,36,0.9)]"></span>AT VILLAGE · &lt;{village.thresholds.at_village_minutes}m</div>
                                            <div><span className="inline-block w-2 h-2 rounded-full bg-amber-500/40 mr-2"></span>LEAVING · &lt;{village.thresholds.leaving_minutes}m</div>
                                            <div><span className="inline-block w-2 h-2 rounded-full bg-cyan-400/60 mr-2 animate-pulse"></span>ARRIVING · &lt;{village.thresholds.arriving_minutes}m</div>
                                            <div><span className="inline-block w-2 h-2 rounded-full bg-amber-900/60 mr-2"></span>OFF MAP · &lt;24h</div>
                                            <div className="text-[9px] opacity-60 pt-1 border-t border-amber-700/30">
                                                {village.count} agent(s) · refresh 30s
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                    {tab === "telemetry" && (
                        <div data-testid="ops-telemetry" className="absolute inset-0 bg-black overflow-hidden">
                            {/* Ten's ops-board mural as backdrop, dimmed so
                                the live overlay reads cleanly on top. */}
                            <img
                                src="/chamber/ops-board.jpeg"
                                alt="Spy Finance Operations Command — backdrop"
                                className="absolute inset-0 w-full h-full object-cover opacity-40"
                            />
                            <div className="absolute inset-0 bg-black/55" />

                            {telemetryErr && (
                                <div data-testid="ops-telemetry-error" className="absolute top-6 left-1/2 -translate-x-1/2 px-4 py-2 border border-red-500/60 bg-black/80 text-red-300 text-xs">
                                    Telemetry feed: {telemetryErr}
                                </div>
                            )}

                            <div className="relative w-full h-full p-8 grid grid-cols-2 grid-rows-2 gap-6">
                                <TelemetryGrid
                                    telemetry={telemetry}
                                    actionBusy={actionBusy}
                                    actionResult={actionResult}
                                    runAction={runAction}
                                />
                            </div>
                            <div className="absolute bottom-3 right-6 text-[10px] tracking-[0.25em] text-amber-300/60 font-mono">
                                {telemetry ? `LIVE · refreshed ${new Date().toLocaleTimeString()}` : "WARMING…"}
                            </div>
                        </div>
                    )}
                </div>
                <div className="px-5 py-2 border-t border-amber-900/40 bg-black/40 text-amber-300/70 text-[10px] tracking-[0.25em]">
                    IN SERVICE · IN SYNC · IN CONTROL
                </div>
            </div>
        </div>
    );
}

export default function Chamber() {
    const user = getUser();
    const [activeFrame, setActiveFrame] = useState(1);
    const [opsOpen, setOpsOpen] = useState(false);
    const [mobile, setMobile] = useState(false);
    // Iter 31.150 — auto-rotate state.
    const [autoRotate, setAutoRotate] = useState(false);
    // Iter 31.150 — agent station hover state (which portrait is hovered).
    const [hoveredAgent, setHoveredAgent] = useState(null);
    const containerRef = useRef(null);
    // Iter 31.150 — Radio bleed. Pulls live state from the global
    // RadioProvider so the Chamber surfaces an ON AIR pill + the
    // currently-broadcasting performer's portrait pulsing at the dome
    // when audio is playing. Mute toggle controls the same Provider
    // state /radio uses — global single source of truth.
    const radio = useRadio();

    useEffect(() => {
        const check = () => setMobile(window.innerWidth < 1024);
        check();
        window.addEventListener("resize", check);
        return () => window.removeEventListener("resize", check);
    }, []);

    // Iter 31.150 — auto-rotation through the 8 storyboard frames per
    // Two's brief. Pauses when the Ops Board overlay is open (you don't
    // want the chamber spinning while reading Gary's Brain) and when
    // toggled off. Re-advances every AUTO_ROTATE_SECS seconds.
    useEffect(() => {
        if (!autoRotate || opsOpen) return undefined;
        const id = setInterval(() => {
            setActiveFrame((f) => (f % FRAMES.length) + 1);
        }, AUTO_ROTATE_SECS * 1000);
        return () => clearInterval(id);
    }, [autoRotate, opsOpen]);

    const frame = FRAMES[activeFrame - 1];

    const selectFrame = (id) => {
        const f = FRAMES.find((x) => x.id === id);
        setActiveFrame(id);
        if (f?.opensOpsBoard) setOpsOpen(true);
    };

    if (mobile) return <MobileFallback />;

    return (
        <div
            data-testid="chamber-page"
            ref={containerRef}
            className="fixed inset-0 bg-black text-amber-100 overflow-hidden font-mono"
        >
            {/* Layer 1 — panorama. transform-origin shifts give the
                rotation feel without a heavy library. */}
            <div
                data-testid="chamber-backdrop"
                className="absolute inset-0 transition-transform duration-700 ease-out"
                style={{
                    backgroundImage: "url('/chamber/chamber-panorama.jpeg')",
                    backgroundSize: "cover",
                    backgroundPosition: `${frame.x}% ${frame.y}%`,
                    transformOrigin: `${frame.x}% ${frame.y}%`,
                    transform: "scale(1.05)",
                }}
            />
            {/* Vignette / darkening overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/60 pointer-events-none" />

            {/* Hotspots */}
            {FRAMES.map((f) => (
                <button
                    key={f.id}
                    data-testid={`chamber-hotspot-${f.id}`}
                    onClick={() => selectFrame(f.id)}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 group ${activeFrame === f.id ? "z-30" : "z-20"}`}
                    style={{ left: `${f.x}%`, top: `${f.y}%` }}
                    aria-label={f.name}
                >
                    <span
                        className={`block w-3 h-3 rounded-full transition-all ${
                            activeFrame === f.id
                                ? "bg-amber-200 shadow-[0_0_20px_rgba(251,191,36,0.9)]"
                                : "bg-amber-500/60 hover:bg-amber-300 shadow-[0_0_10px_rgba(251,191,36,0.5)]"
                        }`}
                    />
                    <span
                        className={`absolute top-5 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] tracking-[0.25em] px-2 py-0.5 border border-amber-700/60 bg-black/70 transition-opacity ${
                            activeFrame === f.id ? "opacity-100" : "opacity-0 group-hover:opacity-80"
                        }`}
                    >
                        {f.bearing} · F{f.id}
                    </span>
                </button>
            ))}

            {/* Iter 31.150 — Agent stations. Each portrait pinned to
                its frame anchor + slight offset so the dot + portrait
                don't overlap. Hover surfaces a small dossier card.
                Click pans to the agent's frame. */}
            {AGENT_STATIONS.map((agent) => {
                const f = FRAMES.find((x) => x.id === agent.frameId);
                if (!f) return null;
                const active = hoveredAgent === agent.key;
                return (
                    <button
                        key={agent.key}
                        data-testid={`chamber-agent-${agent.key}`}
                        onClick={() => selectFrame(agent.frameId)}
                        onMouseEnter={() => setHoveredAgent(agent.key)}
                        onMouseLeave={() => setHoveredAgent(null)}
                        className="absolute z-25 group"
                        style={{ left: `${f.x}%`, top: `${f.y + 8}%`, transform: "translate(-50%, 0)" }}
                        aria-label={`${agent.name} — ${agent.role}`}
                    >
                        <img
                            src={agent.portrait}
                            alt={agent.name}
                            className={`w-10 h-10 rounded-full border-2 transition-all object-cover bg-black ${
                                active
                                    ? "border-amber-300 shadow-[0_0_20px_rgba(251,191,36,0.9)] scale-110"
                                    : "border-amber-600/70 shadow-[0_0_10px_rgba(251,191,36,0.4)] hover:border-amber-300"
                            }`}
                            onError={(e) => { e.target.style.opacity = "0.3"; }}
                        />
                        {active && (
                            <div
                                data-testid={`chamber-agent-dossier-${agent.key}`}
                                className="absolute left-1/2 -translate-x-1/2 top-12 whitespace-nowrap px-2 py-1 border border-amber-400/60 bg-black/90 text-[10px] tracking-[0.2em] text-amber-100"
                            >
                                <div className="font-bold">{agent.name.toUpperCase()}</div>
                                <div className="opacity-70 text-[9px]">{agent.role}</div>
                            </div>
                        )}
                    </button>
                );
            })}

            {/* Iter 31.150 — Performer dome pulse. When the radio is
                broadcasting, the current performer's portrait pulses
                at its dome position. Reads the same currentPerformerKey
                the PerformerOverlay component reads. */}
            {radio?.playing && PERFORMER_DOME_POSITIONS[radio.currentPerformerKey] && (
                <div
                    data-testid="chamber-performer-dome"
                    className="absolute z-25 pointer-events-none"
                    style={{
                        left: `${PERFORMER_DOME_POSITIONS[radio.currentPerformerKey].x}%`,
                        top: `${PERFORMER_DOME_POSITIONS[radio.currentPerformerKey].y}%`,
                        transform: "translate(-50%, -50%)",
                    }}
                >
                    <img
                        src={`/agent-portraits/${radio.currentPerformerKey}.png`}
                        alt={`Performer ${radio.currentPerformerKey}`}
                        className="w-12 h-12 rounded-full border-2 border-amber-300/80 shadow-[0_0_24px_rgba(251,191,36,0.8)] animate-pulse object-cover bg-black"
                        onError={(e) => { e.target.style.opacity = "0.3"; }}
                    />
                </div>
            )}

            {/* Iter 31.207 — Featured Agent card.
                When the auto-rotation lands on a frame that has an agent
                station, a large card surfaces in the bottom-left corner
                with the agent's portrait, name, role, and a "Dossier"
                button. Gives the chamber cycle visual punch on the 65" TV
                (used to look like nothing was changing because all 8
                frames share one panorama image). */}
            {(() => {
                const featured = AGENT_STATIONS.find((a) => a.frameId === activeFrame);
                if (!featured) return null;
                return (
                    <div
                        data-testid="chamber-featured-agent"
                        className="absolute bottom-12 left-12 z-40 flex items-center gap-5 px-5 py-4 border border-amber-500/70 bg-black/85 backdrop-blur-sm shadow-[0_0_30px_rgba(251,191,36,0.35)] max-w-[640px] transition-opacity duration-500"
                    >
                        <img
                            src={featured.portrait}
                            alt={featured.name}
                            className="w-24 h-24 object-cover border-2 border-amber-600/80 bg-black"
                            onError={(e) => { e.target.style.opacity = "0.3"; }}
                        />
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] tracking-[0.32em] text-amber-300/70 uppercase">
                                Featured Agent · F{activeFrame} · {frame.bearing}
                            </div>
                            <div className="text-3xl font-bold tracking-wide text-amber-100">
                                {featured.name}
                            </div>
                            <div className="text-xs text-amber-300/80 tracking-wider mt-1">
                                {featured.role}
                            </div>
                            <a
                                href={`/agent/dossier/tv?agent=${featured.key}`}
                                className="inline-block mt-3 px-3 py-1.5 border border-amber-400/70 text-[11px] tracking-[0.22em] uppercase text-amber-100 hover:bg-amber-400 hover:text-black"
                                data-testid={`chamber-featured-dossier-${featured.key}`}
                            >
                                Open Dossier →
                            </a>
                        </div>
                    </div>
                );
            })()}

            {/* Toolbar — top */}
            <div className="absolute top-0 left-0 right-0 z-40 flex items-center justify-between px-6 py-4 bg-gradient-to-b from-black/80 to-transparent">
                <div className="flex items-center gap-3">
                    <Compass size={16} className="text-amber-300" />
                    <div>
                        <div className="text-[10px] tracking-[0.3em] opacity-70">HANDLER'S CHAMBER · INTERNAL</div>
                        {/* Iter 31.215 — agent-curated subtitle under the chamber kicker. */}
                        <EasterEggSlot
                            path="chamber"
                            slot="chamber.subtitle"
                            as="div"
                            className="text-[9px] tracking-[0.24em] text-amber-300/60 italic mt-0.5"
                        />
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {/* Iter 31.150 — Radio bleed pill. Shows when audio is
                        playing; surfaces current performer + a mute toggle
                        that flips the global RadioProvider state. */}
                    {radio?.playing && (
                        <div
                            data-testid="chamber-radio-bleed"
                            className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-400/60 bg-black/40 text-[11px] tracking-[0.2em] text-amber-200"
                        >
                            <RadioIcon size={12} className="text-amber-300 animate-pulse" />
                            <span data-testid="chamber-radio-performer">
                                ON AIR · {(radio.currentPerformerKey || "twenty").replace(/_/g, " ").toUpperCase()}
                            </span>
                            <button
                                data-testid="chamber-radio-mute"
                                onClick={() => radio.setMuted(!radio.muted)}
                                className="ml-1 opacity-80 hover:opacity-100"
                                aria-label={radio.muted ? "Unmute Chamber" : "Mute Chamber"}
                            >
                                {radio.muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
                            </button>
                        </div>
                    )}
                    {/* Iter 31.150 — Auto-rotate toggle. Cycles the 8
                        storyboard frames every {AUTO_ROTATE_SECS}s
                        until paused or until the Ops Board opens. */}
                    <button
                        data-testid="chamber-auto-rotate"
                        onClick={() => setAutoRotate((v) => !v)}
                        aria-pressed={autoRotate}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 border text-[11px] tracking-[0.2em] ${
                            autoRotate
                                ? "border-amber-300 text-amber-100 bg-amber-900/30"
                                : "border-amber-700/60 hover:border-amber-300 text-amber-200"
                        }`}
                    >
                        {autoRotate ? <Pause size={12} /> : <Play size={12} />}
                        AUTO-ROTATE
                    </button>
                    {/* Iter 31.207 — TV dossier cycle launch. */}
                    <a
                        href="/agent/dossier/tv"
                        data-testid="chamber-dossier-tv"
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-amber-700/60 hover:border-amber-300 text-[11px] tracking-[0.2em] text-amber-200"
                        title="Open the agent-dossier auto-cycle TV view"
                    >
                        DOSSIER TV
                    </a>
                    <button
                        data-testid="chamber-open-ops"
                        onClick={() => setOpsOpen(true)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-700/60 hover:border-amber-300 text-[11px] tracking-[0.2em] text-amber-200"
                    >
                        <Network size={12} />
                        OPS BOARD
                    </button>
                    <a
                        data-testid="chamber-exit"
                        href="/home"
                        className="inline-flex items-center gap-1 px-3 py-1.5 border border-amber-700/60 hover:border-amber-300 text-[11px] tracking-[0.2em] text-amber-200"
                    >
                        <X size={12} />
                        EXIT
                    </a>
                </div>
            </div>

            {/* Caption strip — bottom */}
            <div className="absolute bottom-0 left-0 right-0 z-40 px-6 py-4 bg-gradient-to-t from-black/85 to-transparent flex items-center justify-between gap-6">
                <button
                    data-testid="chamber-prev"
                    onClick={() => selectFrame(((activeFrame - 2 + FRAMES.length) % FRAMES.length) + 1)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-amber-300/70 hover:text-amber-100 text-[11px] tracking-[0.2em]"
                >
                    <ChevronLeft size={12} />
                    PREV
                </button>
                <div data-testid="chamber-caption" className="text-center flex-1 max-w-3xl">
                    <div className="text-[10px] tracking-[0.3em] opacity-60 mb-1">FRAME {frame.id} · {frame.name}</div>
                    <div className="text-sm tracking-wide opacity-90">{frame.caption}</div>
                </div>
                <button
                    data-testid="chamber-next"
                    onClick={() => selectFrame((activeFrame % FRAMES.length) + 1)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-amber-300/70 hover:text-amber-100 text-[11px] tracking-[0.2em]"
                >
                    NEXT
                    <ChevronRight size={12} />
                </button>
            </div>

            <OpsBoardOverlay open={opsOpen} onClose={() => setOpsOpen(false)} />

            {/* Operator stamp — bottom-right */}
            <div className="absolute bottom-20 right-6 z-30 text-amber-300/40 text-[10px] tracking-[0.25em] pointer-events-none">
                #{user?.id?.slice(0, 6) || "—"} · IN SERVICE · IN SYNC · IN CONTROL
            </div>
        </div>
    );
}
